import React, { useEffect, useState, useContext } from 'react'
import { FiMapPin, FiHome } from 'react-icons/fi'
import { getCommunityList } from '../services/Api'
import { Link } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext' 
import { GiPalmTree, GiMountainCave, GiIsland, GiModernCity } from 'react-icons/gi'
import { FaCity, FaMosque } from 'react-icons/fa'
import '../index.css'
import { useTranslation } from 'react-i18next';

export default function Communities() {
  const BATCH_SIZE = 6
  const MOBILE_LIMIT = 6
  const emirateTabs = ['All', 'Dubai', 'Sharjah', 'Ajman', 'Abu Dhabi', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain']
  const [selectedEmirate, setSelectedEmirate] = useState('All')
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
  const [getCommunity, setGetCommunity] = useState([])
  const { globalVar } = useContext(GlobalContext)
  const [communityCounts, setCommunityCounts] = useState({})
  const [showAll, setShowAll] = useState(false)
  const { t, i18n } = useTranslation();


  const sortedEmirates = [
    'All',
    ...emirateTabs
      .filter((em) => em !== 'All')
      .sort((a, b) => (communityCounts[b] || 0) - (communityCounts[a] || 0))
  ];

  const displayedEmirates = showAll 
    ? sortedEmirates 
    : sortedEmirates.slice(0, MOBILE_LIMIT)



  const fetchCommunityCounts = async () => {
    try {
      const response = await getCommunityList({ params: { count_by_city: 'true' } })
      if (response.success) {
        const countsMap = {}
        response.data.forEach(({ emirate, community_count }) => {
          countsMap[emirate] = community_count
        })
        setCommunityCounts(countsMap)
      }
    } catch (error) {
      console.error('Failed to fetch community counts', error)
    }
  }

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, getCommunity.length))
  }

  const getCommunityData = async (emirate = null) => {
    try {
      const params = emirate && emirate !== 'All' ? { params: { location__emirate: emirate } } : {}
      const response = await getCommunityList(params)
      if (response.success) {
        setGetCommunity(response.data)
        setVisibleCount(BATCH_SIZE)
      } else {
        console.log('Error fetching communities')
      }
    } catch (error) {
      console.error('API error:', error)
    }
  }

  useEffect(() => {
    fetchCommunityCounts()
  }, [])

  useEffect(() => {
    getCommunityData(selectedEmirate)
  }, [selectedEmirate])

  

  const getImageUrl = (path) => (path.startsWith('/media') ? `${globalVar}${path}` : path)

  const communityCount = getCommunity.length
  const totalProjects = getCommunity.reduce(
    (sum, community) => sum + (community.properties_count || 0),
    0
  )

  // Icon Mapping
  const iconMap = {
    Dubai: GiPalmTree,
    Sharjah: FaMosque,
    Ajman: GiIsland,
    'Abu Dhabi': GiModernCity,
    Fujairah: GiMountainCave,
    'Ras Al Khaimah': FaCity,
    'Umm Al Quwain': FaCity,
    All: FaCity,
  }

  const iconColorMap = {
    Dubai: 'text-emerald-600',
    Sharjah: 'text-blue-600',
    Ajman: 'text-orange-500',
    'Abu Dhabi': 'text-violet-500',
    Fujairah: 'text-red-500',
    'Ras Al Khaimah': 'text-amber-600',
    'Umm Al Quwain': 'text-blue-600',
    All: 'text-gray-500',
  }

  return (
    <section className="pt-20 pb-10 bg-white">
  <h2 className="text-3xl font-bold text-center mb-6 text-gray-900">
         {t('communities.title', 'Where would you like to live in the UAE?')}
      </h2>
      <span className="block text-center mb-14 text-gray-900 mx-4 sm:mx-0 italic font-dark leading-relaxed">
 {t('communities.description', 'Our AI has handpicked top-performing communities across every emirate - just tap and explore.')}    </span>

      {/* Emirates Tabs */}
      <div className="flex justify-center gap-4 mb-10 flex-wrap">
         {displayedEmirates.map((emirate) => {
          const isSelected = selectedEmirate === emirate
          const IconComponent = iconMap[emirate] || FaCity
          const iconClass = `inline-block mr-2 transition-colors duration-300 ${
            isSelected ? 'text-white' : iconColorMap[emirate]
          }`
          return (
            <button
              key={emirate}
              onClick={() => setSelectedEmirate(emirate)}
              className={`
                px-5 py-2 rounded-full text-sm font-medium
                flex items-center justify-center gap-1
                transition duration-300 ease-in-out
                ${isSelected 
                  ? 'bg-teal-600 text-white shadow-lg animate-pulse-glow'
                  : 'bg-gray-200 text-gray-700 hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-300 hover:text-teal-600 hover:scale-105 hover:shadow-md'}
              `}
            >
              <IconComponent className={iconClass} />
              {t(`emirates.${emirate.toLowerCase().replaceAll(" ", "_")}`, emirate)}

              {emirate !== 'All' && communityCounts[emirate] !== undefined && (
                <> ({communityCounts[emirate]})</>
              )}
            </button>
          )
        })}
        {!showAll && sortedEmirates.length > MOBILE_LIMIT && (
          <button
            onClick={() => setShowAll(true)}
            className="px-5 py-2 rounded-full text-sm font-medium bg-teal-500 text-white hover:bg-teal-600 transition shadow-md"
          >
           {t('communities.show_all_cities')} ({sortedEmirates.length})
          </button>
        )}
      </div>

      {/* Live summary */}
      {communityCount > 0 && (
        <div className="max-w-7xl mx-auto px-4 mb-10 text-center">
          <div className="inline-block relative px-6 sm:px-10 py-3 sm:py-4 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 shadow-md sm:shadow-lg text-white font-semibold text-sm sm:text-lg leading-snug sm:leading-normal overflow-hidden w-full max-w-md sm:max-w-full">
            
            {/* Animated sparkles (hidden on small screens if needed) */}
            <span className="hidden sm:block absolute top-1 left-4 w-2 h-2 bg-white rounded-full opacity-70 animate-ping-slow"></span>
            <span className="hidden sm:block absolute bottom-2 right-5 w-3 h-3 bg-white rounded-full opacity-50 animate-ping-slower"></span>

          <span className="whitespace-normal">
  {t('communities.AI_found', 'AI found')}{' '}
  <span className=" decoration-white/70 underline-offset-2">
    {communityCount}
  </span>{' '}
  {t('communities.community_count', communityCount === 1 ? 'community' : 'communities')}{' '}
  {t('communities.in', 'in')}{' '}
  <span className=" decoration-white/70 underline-offset-2">
    {selectedEmirate === 'All' ? t('communities.the_UAE', 'the UAE') : selectedEmirate}
  </span>{' '}
  {t('communities.with', 'with')}{' '}
  <span className=" decoration-white/70 underline-offset-2">
    {totalProjects}
  </span>{' '}
  {t('communities.active', 'active')}{' '}
  {t('communities.project_count', totalProjects === 1 ? 'project' : 'projects')}
</span>

            
          </div>
        </div>
      )}   

      {/* Community Grid */}
      <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {getCommunity.slice(0, visibleCount).map((community) => (
          <Link
            key={community.id}
            to={`/community-detail/${community.slug}`}
            className="block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition"
          >
        <div className="relative h-56">
  <img
    src={
      community.first_property?.images?.[0]?.image
        ? getImageUrl(community.first_property.images[0].image)
        : '/placeholder.jpg'
    }
    alt={community.name}
    loading="lazy"
    className="w-full h-full object-cover"
  />
  <div className="absolute inset-0 bg-black/50" />
  <div className="absolute bottom-4 left-4 text-white rtl:right-4 rtl:left-auto">
    <h3 className="text-xl font-bold leading-snug text-right rtl:text-left">
      {t(`communities.${community.slug}`, community.name)}
    </h3>
    <div className="flex items-center text-sm mt-1">
      <FiHome className="mr-1" />
      <span>{community.properties_count} {t('communities.projects', 'Projects')}</span>
    </div>
  </div>
</div>


            <div className="p-6 space-y-3">
              <div className="flex items-center text-gray-600">
                <FiMapPin className="mr-2" />
               {t(`emirates.${community.location.emirate?.toLowerCase().replaceAll(" ", "_")}`, community.location.emirate)}
              </div>
              {/* <div className="flex items-center text-gray-600">
                <FiHome className="mr-2" />
                <span>{community.developer.name}</span>
              </div> */}
              {community.lowest_price && community.highest_price && (
                <div className="text-sm text-gray-800 font-medium">
                  Min Price : AED {Number(community.lowest_price).toLocaleString()} Max Price : AED{' '}
                  {Number(community.highest_price).toLocaleString()}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {visibleCount < getCommunity.length && (
        <div className="mt-10 text-center">
          <button
            onClick={handleLoadMore}
            className="px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-full transition"
          >
              {t('load_more', 'Load More')}
          </button>
        </div>
      )}
    </section>
  )
}